import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import React__default, { useCallback } from 'react';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu.js';
import { IconNavigational24Search, IconDropdown14Search, IconDropdown14Tick, IconMobileSheet24MiscTick } from '@fabric-app/icons/svg';
import { SelectTriggerButton, SelectChevronDown } from '../Select.js';
import { isTruthy } from '@fabric-app/utils/guards';
import { styled } from 'styled-components';
import { ScrollArea } from '../ScrollArea.js';
import { Spinner } from '../Spinner.js';
import { cssVar } from '../../theme/variables.js';
import { useVirtualGrid } from '../VirtualGrid/useVirtualGrid.js';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { interleave } from '@fabric-app/utils/array';
import { mediaMobile } from '../../constants/styled-utils.js';
import { useResponsive } from '../../hooks/useResponsive.js';
import { DrawerDragHandle, DrawerTitle } from '../Modal/Drawer.js';
import { ResponsiveIcon } from '../ResponsiveIcon.js';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';

const MAX_DISPLAYED_ITEMS = 2;
const VirtualOptions = ({ displayedOptions, value, onSelectItem, onValueChange, scrollAreaRef, desktopIcons = false }) => {
  const { isMobileView } = useResponsive();
  const containerRef = React__default.useRef(null);
  const [containerRect] = useDomContentRect(containerRef);
  const { getVirtualItems, containerDimensions } = useVirtualGrid({
    itemHeight: isMobileView ? 60 : 32,
    itemWidth: containerRect?.width || 100,
    itemsCount: displayedOptions.length,
    columnCount: 1,
    scrollElement: scrollAreaRef.current,
    scrollArea: containerRef.current,
    overscan: 10,
    enabled: containerRect !== null
  });
  return /* @__PURE__ */ jsx(StyledDropdownMenuGroup, {
    ref: containerRef,
    style: {
      height: containerDimensions.height,
      position: "relative"
    },
    children: getVirtualItems().map((virtualItem) => {
      const option = displayedOptions[virtualItem.index];
      const isSelected = option.value === "placeholder" ? value.length === 0 : value.includes(option.value);
      return /* @__PURE__ */ jsxs(DropdownMenu.Item, {
        onSelect: (e) => {
          e.preventDefault();
        },
        onClick: () => {
          if (option.value === "placeholder") {
            onSelectItem?.(option.value, []);
            onValueChange?.([]);
            return;
          }
          const nextValue = value.includes(option.value) ? value.filter((v) => v !== option.value) : [
            ...value,
            option.value
          ];
          onSelectItem?.(option.value, nextValue);
          onValueChange?.(nextValue);
        },
        style: {
          height: virtualItem.height,
          position: "absolute",
          left: 0,
          right: 0,
          transform: `translateY(${virtualItem.startY}px)`
        },
        "data-testid": `multi-select-item-(${option.value})`,
        "data-selected": isSelected,
        disabled: option.disabled,
        children: [
          /* @__PURE__ */ jsx(TickContainer, {
            children: isSelected && /* @__PURE__ */ jsx(ResponsiveIcon, {
              mobile: IconMobileSheet24MiscTick,
              default: IconDropdown14Tick
            })
          }),
          option.icon && /* @__PURE__ */ jsx(DropdownMenu.IconContainer, {
            nonMobileVisible: desktopIcons,
            children: option.icon
          }),
          option.label || option.stringLabel
        ]
      }, option.value);
    })
  });
};
const MultiSelect = ({ options, value: valueProp, onSelectItem, trigger, contentProps, onValueChange, isLoading, searchProps, filterDisplayedOption, displayDivider = ", ", title, closeOnChange = false, minContentWidth = 260, desktopIcons = false, beforeOptions }) => {
  const openState = useBoolState(false);
  const { isMobileView } = useResponsive();
  const [displayedValue, value] = React__default.useMemo(() => {
    const value2 = Array.isArray(valueProp) ? valueProp : [
      valueProp
    ];
    if (value2.length < 1) {
      const option = options.find((item) => item.value === "placeholder");
      return [
        option?.label || option?.stringLabel,
        value2
      ];
    }
    let displayedItems = value2.map((v) => {
      const option = options.find((o) => o.value === v);
      return option?.label || option?.stringLabel;
    }).filter(isTruthy);
    const totalItems = displayedItems.length;
    if (totalItems > MAX_DISPLAYED_ITEMS) {
      displayedItems = displayedItems.slice(0, MAX_DISPLAYED_ITEMS);
    }
    if (displayDivider) {
      displayedItems = interleave(displayedItems, displayDivider);
    }
    if (totalItems > MAX_DISPLAYED_ITEMS) {
      displayedItems.push(/* @__PURE__ */ jsxs(XMore, {
        children: [
          "+",
          totalItems - MAX_DISPLAYED_ITEMS,
          " more"
        ]
      }));
    }
    return [
      displayedItems,
      value2
    ];
  }, [
    valueProp,
    options,
    displayDivider
  ]);
  const displayedOptions = React__default.useMemo(() => {
    if (!filterDisplayedOption) {
      return options;
    }
    return options.filter(filterDisplayedOption);
  }, [
    options,
    filterDisplayedOption
  ]);
  const scrollAreaRef = React__default.useRef(null);
  const handleFalse = openState.handleFalse;
  const handleSelectItem = useCallback((selectedItem, value2) => {
    onSelectItem?.(selectedItem, value2);
    if (closeOnChange) {
      handleFalse();
    }
  }, [
    onSelectItem,
    closeOnChange,
    handleFalse
  ]);
  const TriggerChildren = /* @__PURE__ */ jsxs(Fragment, {
    children: [
      isLoading ? /* @__PURE__ */ jsx(Spinner, {
        color: cssVar["color-text-placeholder"],
        size: 14
      }) : trigger?.icon,
      displayedValue || trigger?.emptyLabel
    ]
  });
  return /* @__PURE__ */ jsxs(DropdownMenu.Base, {
    openProps: openState.modalProps,
    renderTriggerElement: trigger?.render ? (props) => (
      // duplicate check because typescript doesn't infer properly
      trigger?.render && trigger.render({
        ...props,
        children: TriggerChildren
      })
    ) : (props) => /* @__PURE__ */ jsxs(SelectTriggerButton, {
      "data-testid": trigger?.testId,
      variant: "bg-primary",
      as: DropdownMenu.Trigger,
      disabled: isLoading || trigger?.disabled,
      fixedWidth: trigger?.fixedWidth,
      ...props,
      children: [
        TriggerChildren,
        /* @__PURE__ */ jsx(SelectChevronDown, {})
      ]
    }),
    contentProps: {
      align: "start",
      ...contentProps,
      style: {
        padding: 0,
        ...contentProps?.style
      }
    },
    children: [
      /* @__PURE__ */ jsx(DrawerDragHandle, {}),
      title && /* @__PURE__ */ jsx(DropdownMenu.GroupTitle, {
        children: /* @__PURE__ */ jsx(DrawerTitle, {
          children: title
        })
      }),
      searchProps && /* @__PURE__ */ jsxs(SearchbarContainer, {
        children: [
          /* @__PURE__ */ jsx(SearchbarIconContainer, {
            children: /* @__PURE__ */ jsx(ResponsiveIcon, {
              default: IconDropdown14Search,
              mobile: IconNavigational24Search
            })
          }),
          /* @__PURE__ */ jsx(SearchbarInput, {
            autoCapitalize: "off",
            autoFocus: !isMobileView,
            ...searchProps,
            onKeyDown: (e) => {
              e.stopPropagation();
            }
          })
        ]
      }),
      beforeOptions && beforeOptions({
        open: openState.value,
        onOpenChange: openState.set
      }),
      /* @__PURE__ */ jsx(StyledScrollArea, {
        orientation: "vertical",
        style: {
          maxHeight: isMobileView ? "70vh" : "300px",
          minWidth: !isMobileView ? minContentWidth : void 0,
          padding: isMobileView ? `0 1rem` : 5,
          marginTop: isMobileView ? 20 : 0
        },
        asFlex: true,
        scrollbarVariant: "subtle",
        ref: scrollAreaRef,
        children: displayedOptions.length === 0 ? /* @__PURE__ */ jsx(StyledDropdownMenuGroup, {
          children: /* @__PURE__ */ jsx(EmptyState, {
            children: "No options found"
          })
        }) : /* @__PURE__ */ jsx(VirtualOptions, {
          displayedOptions,
          value,
          onSelectItem: handleSelectItem,
          onValueChange,
          scrollAreaRef,
          desktopIcons
        })
      })
    ]
  });
};
const SearchbarContainer = styled(DropdownMenu.Group).withConfig({
  displayName: "MultiSelect__SearchbarContainer",
  componentId: "sc-f210038d-0"
})([
  `height:42px;display:flex;padding:0 10px;border-bottom:1px solid `,
  `;align-items:center;gap:5px;`,
  `{padding:0 1rem;background:`,
  `;border-radius:16px;border-bottom:none;margin:0 1rem;height:44px;gap:0.5rem;}`
], cssVar["color-border-primary"], mediaMobile, cssVar["color-border-tertiary"]);
const SearchbarIconContainer = styled.div.withConfig({
  displayName: "MultiSelect__SearchbarIconContainer",
  componentId: "sc-f210038d-1"
})([
  `padding:0 5px;color:`,
  `;`,
  `{padding:0;width:24px;height:24px;}`
], cssVar["color-text-primary"], mediaMobile);
const SearchbarInput = styled.input.withConfig({
  displayName: "MultiSelect__SearchbarInput",
  componentId: "sc-f210038d-2"
})([
  `border:none;outline:none;font-size:0.875rem;font-weight:500;color:`,
  `;&::placeholder{color:`,
  `;}width:1px;flex-grow:1;background-color:transparent;`,
  `{height:100%;font-size:0.9375rem;}`
], cssVar["color-text-tertiary"], cssVar["color-text-placeholder"], mediaMobile);
const EmptyState = styled.div.withConfig({
  displayName: "MultiSelect__EmptyState",
  componentId: "sc-f210038d-3"
})([
  `display:flex;justify-content:center;align-items:center;padding:10px;font-size:0.875rem;font-weight:400;color:`,
  `;text-align:center;`
], cssVar["color-text-placeholder"]);
const XMore = styled.div.withConfig({
  displayName: "MultiSelect__XMore",
  componentId: "sc-f210038d-4"
})([
  `background:`,
  `;padding:0.25rem 0.375rem;border-radius:5px;font-size:0.75rem;line-height:1em;`
], cssVar["color-bg-tertiary"]);
const TickContainer = styled.div.withConfig({
  displayName: "MultiSelect__TickContainer",
  componentId: "sc-f210038d-5"
})([
  `display:flex;align-items:center;justify-content:center;width:0.875rem;height:0.875rem;color:`,
  `;`,
  `{order:100;margin-left:auto;width:1.5rem;height:1.5rem;}`
], cssVar["color-text-primary"], mediaMobile);
const StyledDropdownMenuGroup = styled(DropdownMenu.Group).withConfig({
  displayName: "MultiSelect__StyledDropdownMenuGroup",
  componentId: "sc-f210038d-6"
})([
  ``
]);
const StyledScrollArea = styled(ScrollArea.Default).withConfig({
  displayName: "MultiSelect__StyledScrollArea",
  componentId: "sc-f210038d-7"
})([
  ``,
  `{margin-top:1.25rem;[data-radix-scroll-area-viewport]{border-radius:1rem;}}`
], mediaMobile);

export { MultiSelect };
//# sourceMappingURL=MultiSelect.js.map
